import { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CITIES } from '@/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const applicationSchema = z.object({
  full_name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone must be at least 10 characters'),
  city_preference: z.string().min(1, 'Please select a city'),
  skill_level: z.string().min(1, 'Please select a skill level'),
  portfolio_links: z.string().optional(),
  goals: z.string().min(20, 'Please provide more details'),
  availability_dates: z.string().min(1, 'Please provide availability'),
  why_ai_house: z.string().min(50, 'Please provide at least 50 characters'),
  what_will_you_build: z.string().min(50, 'Please provide at least 50 characters'),
  team_or_solo: z.string().min(1, 'Please select an option'),
  resume_url: z.string().optional(),
  pitch_deck_url: z.string().optional()
});

export default function Apply() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
    resolver: zodResolver(applicationSchema)
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/applications`, data);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Error submitting application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center py-24" data-testid="application-confirmation">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl mx-auto px-4 text-center"
        >
          <CheckCircle className="text-accent mx-auto mb-6" size={64} />
          <h1 className="text-4xl font-unbounded font-black mb-6">Application Submitted!</h1>
          <p className="text-lg font-manrope text-gray-700 mb-8">
            Thank you for applying to AI House USA. We've received your application and will review it shortly. You'll hear from us within 2 weeks.
          </p>
          <Button asChild className="bg-black text-white px-8 py-6 font-bold uppercase">
            <a href="/">Return Home</a>
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-white py-24" data-testid="apply-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-5xl font-unbounded font-black mb-4" data-testid="apply-title">Apply to AI House USA</h1>
          <p className="text-lg font-manrope text-gray-700 mb-12">
            Join the next cohort and ship real AI projects in 30 days.
          </p>

          <div className="mb-12" data-testid="step-indicator">
            <div className="flex items-center justify-between">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center flex-1">
                  <div
                    className={`w-10 h-10 flex items-center justify-center font-bold ${
                      step >= s ? 'bg-accent text-white' : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {s}
                  </div>
                  {s < 3 && <div className={`flex-1 h-1 ${step > s ? 'bg-accent' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            {step === 1 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6" data-testid="step-1">
                <h2 className="text-2xl font-unbounded font-bold mb-6">Personal Information</h2>
                
                <div>
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input id="full_name" {...register('full_name')} className="mt-2" data-testid="full-name-input" />
                  {errors.full_name && <p className="text-red-600 text-sm mt-1">{errors.full_name.message}</p>}
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" {...register('email')} className="mt-2" data-testid="email-input" />
                  {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input id="phone" {...register('phone')} className="mt-2" data-testid="phone-input" />
                  {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone.message}</p>}
                </div>

                <div>
                  <Label htmlFor="city_preference">City Preference *</Label>
                  <Select onValueChange={(value) => setValue('city_preference', value)}>
                    <SelectTrigger className="mt-2" data-testid="city-preference-select">
                      <SelectValue placeholder="Select a city" />
                    </SelectTrigger>
                    <SelectContent>
                      {CITIES.map((city) => (
                        <SelectItem key={city.id} value={city.name}>{city.name} {city.status === 'live' ? '(Live)' : '(Coming Soon)'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.city_preference && <p className="text-red-600 text-sm mt-1">{errors.city_preference.message}</p>}
                </div>

                <div>
                  <Label htmlFor="skill_level">Skill Level *</Label>
                  <Select onValueChange={(value) => setValue('skill_level', value)}>
                    <SelectTrigger className="mt-2" data-testid="skill-level-select">
                      <SelectValue placeholder="Select your skill level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner (New to AI)</SelectItem>
                      <SelectItem value="intermediate">Intermediate (Some AI experience)</SelectItem>
                      <SelectItem value="advanced">Advanced (Built AI products)</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.skill_level && <p className="text-red-600 text-sm mt-1">{errors.skill_level.message}</p>}
                </div>

                <div>
                  <Label htmlFor="portfolio_links">Portfolio/LinkedIn/GitHub (Optional)</Label>
                  <Input id="portfolio_links" {...register('portfolio_links')} className="mt-2" data-testid="portfolio-input" />
                </div>

                <Button
                  type="button"
                  onClick={() => setStep(2)}
                  className="w-full bg-accent text-white px-8 py-6 font-bold uppercase hover:bg-accent-hover"
                  data-testid="next-step-1-button"
                >
                  Next <ArrowRight className="ml-2" size={20} />
                </Button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6" data-testid="step-2">
                <h2 className="text-2xl font-unbounded font-bold mb-6">Goals & Availability</h2>

                <div>
                  <Label htmlFor="goals">What are your goals for the program? *</Label>
                  <Textarea id="goals" {...register('goals')} rows={4} className="mt-2" data-testid="goals-textarea" />
                  {errors.goals && <p className="text-red-600 text-sm mt-1">{errors.goals.message}</p>}
                </div>

                <div>
                  <Label htmlFor="availability_dates">Availability (e.g., Q1 2025, March-April) *</Label>
                  <Input id="availability_dates" {...register('availability_dates')} className="mt-2" data-testid="availability-input" />
                  {errors.availability_dates && <p className="text-red-600 text-sm mt-1">{errors.availability_dates.message}</p>}
                </div>

                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="flex-1 px-8 py-6 font-bold uppercase"
                    data-testid="back-step-2-button"
                  >
                    <ArrowLeft className="mr-2" size={20} /> Back
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setStep(3)}
                    className="flex-1 bg-accent text-white px-8 py-6 font-bold uppercase hover:bg-accent-hover"
                    data-testid="next-step-2-button"
                  >
                    Next <ArrowRight className="ml-2" size={20} />
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6" data-testid="step-3">
                <h2 className="text-2xl font-unbounded font-bold mb-6">Short Answers</h2>

                <div>
                  <Label htmlFor="why_ai_house">Why AI House? (min 50 characters) *</Label>
                  <Textarea id="why_ai_house" {...register('why_ai_house')} rows={4} className="mt-2" data-testid="why-ai-house-textarea" />
                  {errors.why_ai_house && <p className="text-red-600 text-sm mt-1">{errors.why_ai_house.message}</p>}
                </div>

                <div>
                  <Label htmlFor="what_will_you_build">What will you build in 30 days? (min 50 characters) *</Label>
                  <Textarea id="what_will_you_build" {...register('what_will_you_build')} rows={4} className="mt-2" data-testid="what-will-you-build-textarea" />
                  {errors.what_will_you_build && <p className="text-red-600 text-sm mt-1">{errors.what_will_you_build.message}</p>}
                </div>

                <div>
                  <Label htmlFor="team_or_solo">Prefer to work in a team or solo? *</Label>
                  <Select onValueChange={(value) => setValue('team_or_solo', value)}>
                    <SelectTrigger className="mt-2" data-testid="team-or-solo-select">
                      <SelectValue placeholder="Select preference" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="team">Team</SelectItem>
                      <SelectItem value="solo">Solo</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.team_or_solo && <p className="text-red-600 text-sm mt-1">{errors.team_or_solo.message}</p>}
                </div>

                <div>
                  <Label htmlFor="resume_url">Resume URL (Optional)</Label>
                  <Input id="resume_url" {...register('resume_url')} className="mt-2" data-testid="resume-url-input" />
                </div>

                <div>
                  <Label htmlFor="pitch_deck_url">Pitch Deck URL (Optional)</Label>
                  <Input id="pitch_deck_url" {...register('pitch_deck_url')} className="mt-2" data-testid="pitch-deck-url-input" />
                </div>

                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => setStep(2)}
                    variant="outline"
                    className="flex-1 px-8 py-6 font-bold uppercase"
                    data-testid="back-step-3-button"
                  >
                    <ArrowLeft className="mr-2" size={20} /> Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-accent text-white px-8 py-6 font-bold uppercase hover:bg-accent-hover"
                    data-testid="submit-application-button"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </Button>
                </div>
              </motion.div>
            )}
          </form>
        </motion.div>
      </div>
    </div>
  );
}
